import { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { CheckIcon } from '@heroicons/react/outline'
import { IoStar, IoStarOutline } from 'react-icons/io5'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { get, keys } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'

const RATING_VALUES = [1, 2, 3, 4, 5]

const StarRating = ({ value = 0, onChange }) => {
  const [hover, setHover] = useState(0)
  const isPreviewing = hover > 0

  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {RATING_VALUES.map(star => {
        // While hovering, preview up to the hovered star; otherwise show the committed value.
        const filled = isPreviewing ? star <= hover : star <= value
        const Icon = filled ? IoStar : IoStarOutline
        return (
          <button
            key={star}
            type="button"
            aria-pressed={value === star}
            aria-label={`${star}`}
            // Clicking the currently-selected star again clears the rating back to 0.
            onClick={() => onChange(star === value ? 0 : star)}
            onMouseEnter={() => setHover(star)}
            className="cursor-pointer rounded p-0.5 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          >
            <Icon
              className={classNames(
                'h-6 w-6 transition-colors',
                // committed value = solid amber, hover preview = lighter amber, empty = grey
                (!isPreviewing && filled && 'text-amber-400') || (isPreviewing && filled && 'text-amber-300') || 'text-slate-300'
              )}
            />
          </button>
        )
      })}
    </div>
  )
}

// Keep in sync with backend src/services/officeEvaluation.service.ts
const SALES_FIELDS = ['carAvailableOnRequest', 'officeCooperation', 'bookingAcceptedUnder30Min', 'bookingAcceptedWithin10Min', 'responseSpeed', 'punctuality']

// Per-violation raw penalty (in the 30-point sales scale), matching the evaluation sheet.
const VIOLATION_WEIGHTS = {
  delayOver30MinCollecting: 3,
  carNotProvided48h: 5,
  delayDeliveringUnder30Min: 2,
  delayDeliveringOver30Min: 5,
  officeNoResponseUntilPickup: 5,
}
const VIOLATION_FIELDS = keys(VIOLATION_WEIGHTS)

const emptySales = () => SALES_FIELDS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})
const emptyViolations = () => VIOLATION_FIELDS.reduce((acc, key) => ({ ...acc, [key]: false }), {})

// score = MAX(0, (sum(sales) - sum(violation weights for "Yes")) * 50/30)
const computeScore = (sales, violations) => {
  const salesSum = SALES_FIELDS.reduce((sum, key) => sum + (Number(sales?.[key]) || 0), 0)
  const rawPenalty = VIOLATION_FIELDS.reduce((penalty, key) => penalty + (violations?.[key] ? VIOLATION_WEIGHTS[key] : 0), 0)
  const score = Math.max(0, (salesSum - rawPenalty) * (50 / 30))
  return Math.round(score * 100) / 100
}

const resolveVerdict = score => {
  if (score >= 40) return 'Excellent'
  if (score >= 30) return 'Good'
  if (score >= 20) return 'Acceptable'
  return 'Rejected'
}

const verdictColor = verdict =>
  ({
    Excellent: 'bg-green-100 text-green-700 ring-green-200',
    Good: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
    Acceptable: 'bg-amber-100 text-amber-700 ring-amber-200',
    Rejected: 'bg-red-100 text-red-700 ring-red-200',
  }[verdict] || 'bg-slate-100 text-slate-700 ring-slate-200')

const OfficeEvaluationModal = ({ bookingId, close, onSaved }) => {
  const { t } = useTranslation()
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // The evaluator is always the logged-in user performing the evaluation.
  const evaluatorName = userFromStore?.fullName || ''

  const [activeStep, setActiveStep] = useState(1)
  const [sales, setSales] = useState(emptySales())
  const [violations, setViolations] = useState(emptyViolations())
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)

  const steps = useMemo(
    () => [
      { id: 1, key: 'sales' },
      { id: 2, key: 'violations' },
      { id: 3, key: 'result' },
    ],
    []
  )

  // Prefill from an existing evaluation (edit mode).
  useEffect(() => {
    let active = true
    const load = async () => {
      if (!bookingId) {
        setLoading(false)
        return
      }
      try {
        const response = await apiGet(`office-evaluation/${bookingId}`, {}, false)
        const data = response?.data
        if (active && data?._id) {
          setSales({ ...emptySales(), ...(data.sales || {}) })
          setViolations({ ...emptyViolations(), ...(data.violations || {}) })
          setNotes(data.notes || '')
        }
      } catch (error) {
        // Non-blocking: a missing evaluation just means "create new"
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [bookingId])

  const score = computeScore(sales, violations)
  const verdict = resolveVerdict(score)

  // Unrated items count as 0, so no per-step rating requirement blocks progress.
  const goNext = () => setActiveStep(step => Math.min(step + 1, steps.length))
  const goBack = () => setActiveStep(step => Math.max(step - 1, 1))

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const response = await apiPost('office-evaluation', {
        bookingId,
        sales,
        violations,
        notes,
        evaluatorName,
      })
      if (response?.success) {
        notify.success(t('officeEvaluation.messages.saved'))
        onSaved?.()
        close?.()
      }
    } catch (error) {
      notify.error(error?.message || t('officeEvaluation.messages.saveFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Stepper */}
      <div className="flex items-center justify-between px-1 pb-4">
        {steps.map((step, index) => (
          <div key={step.id} className="relative flex w-full flex-col items-center">
            {index < steps.length - 1 && (
              <div className={classNames('absolute top-4 left-[calc(50%+1rem)] h-0.5 w-[calc(100%-2rem)]', activeStep > step.id ? 'bg-dark-600' : 'bg-slate-200')} />
            )}
            <div
              className={classNames(
                'z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors',
                activeStep > step.id && 'bg-dark-600 text-white',
                activeStep === step.id && 'border-2 border-dark-600 bg-white text-dark-600',
                activeStep < step.id && 'border-2 border-slate-200 bg-white text-slate-400'
              )}
            >
              {activeStep > step.id ? <CheckIcon className="h-4 w-4" /> : step.id}
            </div>
            <span className={classNames('mt-1.5 text-center text-[11px] font-semibold', activeStep >= step.id ? 'text-dark-600' : 'text-slate-500')}>
              {t(`officeEvaluation.steps.${step.key}`)}
            </span>
          </div>
        ))}
      </div>

      {/* Body */}
      <div className="min-h-[280px] flex-1 overflow-y-auto px-1">
        {loading ? (
          <div className="flex h-full min-h-[280px] items-center justify-center text-sm text-slate-500">{t('officeEvaluation.loading')}</div>
        ) : (
          <>
            {activeStep === 1 && (
              <div className="space-y-2.5">
                <p className="text-xs text-slate-500">{t('officeEvaluation.sales.hint')}</p>
                {SALES_FIELDS.map(key => (
                  <div
                    key={key}
                    className="flex flex-col gap-2 rounded-lg border border-slate-100 bg-white px-4 py-3 shadow-sm transition-colors hover:border-slate-200 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span className="text-sm font-medium text-slate-700">{t(`officeEvaluation.sales.${key}`)}</span>
                    <div className="flex items-center gap-3">
                      <StarRating value={sales[key]} onChange={value => setSales(prev => ({ ...prev, [key]: value }))} />
                      <span className="w-4 text-right text-sm font-semibold text-slate-400">{sales[key] || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeStep === 2 && (
              <div className="space-y-2.5">
                <p className="text-xs text-slate-500">{t('officeEvaluation.violations.hint')}</p>
                {VIOLATION_FIELDS.map(key => (
                  <div
                    key={key}
                    className="flex flex-col gap-2 rounded-lg border border-slate-100 bg-white px-4 py-3 shadow-sm transition-colors hover:border-slate-200 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      {t(`officeEvaluation.violations.${key}`)}
                      <span className="shrink-0 rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-500">−{VIOLATION_WEIGHTS[key]}</span>
                    </span>
                    <div className="flex shrink-0 self-start overflow-hidden rounded-md ring-1 ring-slate-200 sm:self-auto">
                      <button
                        type="button"
                        onClick={() => setViolations(prev => ({ ...prev, [key]: false }))}
                        className={classNames('px-3 py-1 text-xs font-semibold', !violations[key] ? 'bg-green-600 text-white' : 'bg-white text-slate-600')}
                      >
                        {t('officeEvaluation.violations.no')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setViolations(prev => ({ ...prev, [key]: true }))}
                        className={classNames('px-3 py-1 text-xs font-semibold', violations[key] ? 'bg-red-600 text-white' : 'bg-white text-slate-600')}
                      >
                        {t('officeEvaluation.violations.yes')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{t('officeEvaluation.result.score')}</div>
                    <div className="text-2xl font-bold text-slate-900">
                      {score}
                      <span className="text-base font-medium text-slate-400"> / 50</span>
                    </div>
                  </div>
                  <span className={classNames('rounded-full px-3 py-1 text-sm font-semibold ring-1', verdictColor(verdict))}>{t(`officeEvaluation.verdict.${verdict}`)}</span>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="evaluationNotes">
                    {t('officeEvaluation.result.notes')}
                  </label>
                  <textarea
                    id="evaluationNotes"
                    rows={4}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-dark-500 focus:outline-none focus:ring-1 focus:ring-dark-500"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder={t('officeEvaluation.result.notesPlaceholder')}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
        <button
          type="button"
          className="inline-flex items-center rounded-md border shadow px-4 py-2 text-sm font-medium text-dark-500 bg-white transition hover:bg-gray-50 disabled:opacity-50"
          onClick={() => (activeStep === 1 ? close?.() : goBack())}
          disabled={submitting}
        >
          {activeStep === 1 ? t('officeEvaluation.buttons.cancel') : t('officeEvaluation.buttons.back')}
        </button>
        {activeStep < steps.length ? (
          <button
            type="button"
            className="inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white bg-dark-600 hover:bg-dark-500 focus:outline-none transition disabled:opacity-50"
            onClick={goNext}
            disabled={loading}
          >
            {t('officeEvaluation.buttons.next')}
          </button>
        ) : (
          <button
            type="button"
            className="inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white bg-dark-600 hover:bg-dark-500 focus:outline-none transition disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? t('officeEvaluation.buttons.saving') : t('officeEvaluation.buttons.save')}
          </button>
        )}
      </div>
    </div>
  )
}

export default OfficeEvaluationModal
